import clsx from 'clsx'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { initials } from '@/lib/format'

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={clsx('animate-spin', className)} aria-hidden />
}

export function LoadingScreen({ label }: { label?: string }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 py-20 text-ink-muted">
      <Spinner className="h-7 w-7 accent-text" />
      {label && <p className="text-sm">{label}</p>}
    </div>
  )
}

export function Splash() {
  return (
    <div className="app-shell items-center justify-center">
      <Spinner className="h-8 w-8 accent-text" />
    </div>
  )
}

export function EmptyState({
  icon: Icon,
  title,
  subtitle,
}: {
  icon?: React.ComponentType<{ className?: string }>
  title: string
  subtitle?: string
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-16 text-center">
      {Icon && (
        <div className="mb-1 flex h-12 w-12 items-center justify-center rounded-full bg-surface-sunken text-ink-faint">
          <Icon className="h-6 w-6" />
        </div>
      )}
      <p className="font-semibold text-ink">{title}</p>
      {subtitle && <p className="max-w-xs text-sm text-ink-muted">{subtitle}</p>}
    </div>
  )
}

export function ErrorNote({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="m-4 rounded-xl border border-danger/20 bg-danger/5 p-4 text-sm text-danger">
      <p>{message}</p>
      {onRetry && (
        <button className="mt-2 font-semibold underline" onClick={onRetry}>
          Reintentar
        </button>
      )}
    </div>
  )
}

/** Input tipo password con botón de mostrar/ocultar (ojo). Recibe los mismos props que un
 *  <input> nativo salvo `type`, que lo maneja el propio componente. */
export function PasswordInput({
  className,
  ...props
}: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  const [visible, setVisible] = useState(false)
  return (
    <div className="relative">
      <input {...props} type={visible ? 'text' : 'password'} className={clsx('field-input pr-11', className)} />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        tabIndex={-1}
        aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
        className="absolute right-1 top-1/2 -translate-y-1/2 rounded-lg p-2 text-ink-faint hover:text-ink-muted"
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  )
}

export function Avatar({ name, className }: { name: string; className?: string }) {
  return (
    <div
      className={clsx(
        'accent-bg-soft flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold accent-text',
        className
      )}
    >
      {initials(name)}
    </div>
  )
}

export function BrandLogo({
  logoUrl,
  appName,
  className,
}: {
  logoUrl?: string
  appName: string
  className?: string
}) {
  if (logoUrl) {
    return <img src={logoUrl} alt={appName} className={clsx('h-8 w-auto object-contain', className)} />
  }
  // Placeholder con iniciales del nombre de la app (§5.3).
  return (
    <div
      className={clsx(
        'flex h-8 items-center rounded-lg px-2.5 text-sm font-bold text-[color:var(--color-accent-contrast)]',
        className
      )}
      style={{ backgroundColor: 'var(--color-accent)' }}
    >
      {appName}
    </div>
  )
}
